import React, { useState } from 'react'
import { X, ChevronUp, ChevronDown } from 'lucide-react'
import { PROPERTY_TYPES, getAvailableRegions, isValidRegion } from '../helpers/propertyTypeHelpers'
import { usePropertyMetadata } from '../contexts/PropertyMetadataContext'
import LocationFilter from './LocationFilter'

const Section = ({ title, initiallyOpen = false, hasActiveFilters = false, children }) => {
  const [isOpen, setIsOpen] = useState(initiallyOpen || hasActiveFilters)

  return (
    <div className="border-b border-gray-200 last:border-0">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center justify-between w-full py-4 text-left">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-medium">{title}</h3>
          {hasActiveFilters && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs font-medium">Aktivní</span>}
        </div>
        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>
      {isOpen && <div className="pb-4">{children}</div>}
    </div>
  )
}

const FilterModal = ({
  isOpen,
  onClose,
  activeFiltersCount,
  // Type and region
  selectedType,
  selectedRegion,
  selectedOfferType,
  // Districts
  selectedDistricts,
  onDistrictsChange,
  selectedMunicipalities,
  onMunicipalitiesChange,
  // Layouts
  selectedLayouts,
  onLayoutChange,
  // Display options
  selectedDisplayOptions,
  onDisplayOptionsChange,
  // Date range
  selectedDateRange,
  onDateRangeChange,
  // Price range
  minPrice,
  maxPrice,
  onPriceChange,
  // Navigation
  navigate,
}) => {
  const { filterConditions } = usePropertyMetadata()
  // Temporary state for changes
  const [tempType, setTempType] = useState(selectedType)
  const [tempRegion, setTempRegion] = useState(selectedRegion)
  const [tempDistricts, setTempDistricts] = useState(selectedDistricts)
  const [tempMunicipalities, setTempMunicipalities] = useState(selectedMunicipalities)
  const [tempLayouts, setTempLayouts] = useState(selectedLayouts)
  const [tempDisplayOptions, setTempDisplayOptions] = useState(selectedDisplayOptions)
  const [tempDateRange, setTempDateRange] = useState(selectedDateRange)
  const [tempPriceRange, setTempPriceRange] = useState({ min: minPrice, max: maxPrice })

  // Reset temp state when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setTempType(selectedType)
      setTempRegion(selectedRegion)
      setTempDistricts(selectedDistricts)
      setTempMunicipalities(selectedMunicipalities)
      setTempLayouts(selectedLayouts)
      setTempDisplayOptions(selectedDisplayOptions)
      setTempDateRange(selectedDateRange)
      setTempPriceRange({ min: minPrice, max: maxPrice })
    }
  }, [isOpen, selectedType, selectedRegion, selectedDistricts, selectedMunicipalities, selectedLayouts, selectedDisplayOptions, selectedDateRange, minPrice, maxPrice])

  if (!isOpen) return null

  const handleApplyChanges = () => {
    // Collect all changes
    const hasDistrictsChange = JSON.stringify(tempDistricts) !== JSON.stringify(selectedDistricts)
    const hasMunicipalitiesChange = JSON.stringify(tempMunicipalities) !== JSON.stringify(selectedMunicipalities)
    const hasLayoutsChange = JSON.stringify(tempLayouts) !== JSON.stringify(selectedLayouts)
    const hasOptionsChange = JSON.stringify(tempDisplayOptions) !== JSON.stringify(selectedDisplayOptions)
    const hasDateChange = JSON.stringify(tempDateRange) !== JSON.stringify(selectedDateRange)
    const hasPriceChange = tempPriceRange.min !== minPrice || tempPriceRange.max !== maxPrice

    // Build URL parameters
    const params = new URLSearchParams(window.location.search)

    if (tempDistricts.length > 0) {
      params.set('districts', tempDistricts.join(','))
    } else {
      params.delete('districts')
    }

    if (tempMunicipalities.length > 0) params.set('municipalities', tempMunicipalities.join(','))
    else params.delete('municipalities')

    if (tempLayouts.length > 0) {
      params.set('layouts', tempLayouts.map((l) => l.replace('+', '')).join(','))
    } else {
      params.delete('layouts')
    }

    if (tempDisplayOptions.length > 0) {
      params.set('options', tempDisplayOptions.join(','))
    } else {
      params.delete('options')
    }

    if (tempPriceRange.min > 0) params.set('minPrice', tempPriceRange.min.toString())
    else params.delete('minPrice')

    if (tempPriceRange.max < 30000000) params.set('maxPrice', tempPriceRange.max.toString())
    else params.delete('maxPrice')

    const queryString = params.toString()

    // Apply all changes
    if (hasDistrictsChange) {
      onDistrictsChange(tempDistricts)
    }
    if (hasMunicipalitiesChange) onMunicipalitiesChange(tempMunicipalities)
    if (hasLayoutsChange) {
      onLayoutChange(tempLayouts)
    }
    if (hasOptionsChange) {
      onDisplayOptionsChange(tempDisplayOptions)
    }
    if (hasDateChange) {
      onDateRangeChange(tempDateRange)
    }
    if (hasPriceChange) {
      onPriceChange(tempPriceRange.min, tempPriceRange.max)
    }

    // The URL is the durable representation of the complete filter set.
    navigate(`/${selectedOfferType}/${tempType}/${tempRegion}${queryString ? `?${queryString}` : ''}`)

    onClose()
  }

  const handleTypeChange = (newType) => {
    setTempType(newType)
    // If current region is not available for the new type, select the first available region
    if (!isValidRegion(newType, tempRegion)) {
      setTempRegion(getAvailableRegions(newType)[0].value)
    }
    setTempMunicipalities([])
    setTempDistricts([])
  }

  const regions = getAvailableRegions(tempType)

  const layouts = ['1+kk', '1+1', '2+kk', '2+1', '3+kk', '3+1', '4+kk', '4+1', '5+kk', '5+1', '6+kk', '6+1']

  const displayOptions = [
    { value: 'osobní', label: 'Osobní' },
    { value: 'hideNoPrice', label: 'Skrýt bez ceny' },
    { value: 'hideInactive', label: 'Skrýt neaktivní' },
    { value: 'showComments', label: 'Zobrazit s komentáři' },
    { value: 'showAnalysis', label: 'Zobrazit s analýzou' },
  ]
  const constructionOptions = filterConditions.map(({ filterKey, label }) => ({ value: filterKey, label }))
  const hasDisplayFilters = displayOptions.some(({ value }) => tempDisplayOptions.includes(value))
  const hasConstructionFilters = constructionOptions.some(({ value }) => tempDisplayOptions.includes(value))

  const handleLayoutChange = (layout) => {
    setTempLayouts(tempLayouts.includes(layout) ? tempLayouts.filter((x) => x !== layout) : [...tempLayouts, layout])
  }

  const handleDisplayOptionChange = (option) => {
    setTempDisplayOptions(tempDisplayOptions.includes(option) ? tempDisplayOptions.filter((x) => x !== option) : [...tempDisplayOptions, option])
  }

  const clearFilters = () => {
    setTempMunicipalities([])
    setTempDistricts([])
    setTempLayouts([])
    setTempDisplayOptions([])
    setTempDateRange({ start: null, end: null })
    setTempPriceRange({ min: 0, max: 30000000 })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center sm:items-center sm:p-6 bg-black/25">
      <div className="relative flex h-full w-full flex-col bg-white sm:h-auto sm:max-h-[92vh] sm:w-[min(760px,calc(100vw-48px))] sm:rounded-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 sm:rounded-t-2xl">
          <button onClick={onClose} className="p-2 -ml-2">
            <X size={24} />
          </button>
          <h2 className="text-lg font-medium">Filtry</h2>
          <button type="button" onClick={clearFilters} className="text-sm text-gray-500 hover:text-gray-900">Vymazat vše</button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-4">
            {/* Property type section */}
            <Section title="Typ nemovitosti" initiallyOpen={true}>
              <div className="grid grid-cols-2 gap-2">
                {PROPERTY_TYPES.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => handleTypeChange(type.value)}
                    className={`px-4 py-3 border rounded-lg text-left transition-colors ${
                      tempType === type.value ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </Section>

            <Section title="Lokalita" initiallyOpen={true} hasActiveFilters={tempMunicipalities.length > 0 || tempDistricts.length > 0}>
              <LocationFilter
                propertyType={tempType}
                offerType={selectedOfferType}
                regions={regions}
                region={tempRegion}
                onRegionChange={setTempRegion}
                municipalities={tempMunicipalities}
                onMunicipalitiesChange={setTempMunicipalities}
                parts={tempDistricts}
                onPartsChange={setTempDistricts}
              />
            </Section>

            {/* Price section */}
            <Section title="Cena" hasActiveFilters={tempPriceRange.min > 0 || tempPriceRange.max < 30000000}>
              <div className="px-3">
                <div className="flex items-center justify-between mb-6">
                  <div className="w-[45%]">
                    <label className="block text-sm text-gray-700 mb-1">Od</label>
                    <input
                      type="number"
                      value={tempPriceRange.min}
                      onChange={(e) => setTempPriceRange((prev) => ({ ...prev, min: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="0"
                    />
                  </div>
                  <div className="w-[45%]">
                    <label className="block text-sm text-gray-700 mb-1">Do</label>
                    <input
                      type="number"
                      value={tempPriceRange.max}
                      onChange={(e) => setTempPriceRange((prev) => ({ ...prev, max: parseInt(e.target.value) || 30000000 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="30 000 000"
                    />
                  </div>
                </div>
                <div className="text-sm text-gray-500 text-center">{selectedOfferType === 'pronajem' ? 'Kč/měsíc' : 'Kč'}</div>
              </div>
            </Section>

            {/* Layout section */}
            <Section title="Dispozice" hasActiveFilters={tempLayouts.length > 0}>
              <div className="grid grid-cols-3 gap-2">
                {layouts.map((layout) => (
                  <button
                    key={layout}
                    onClick={() => handleLayoutChange(layout)}
                    className={`px-4 py-3 border rounded-lg text-left transition-colors ${
                      tempLayouts.includes(layout) ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {layout}
                  </button>
                ))}
              </div>
            </Section>

            {/* Construction condition section */}
            <Section title="Stavba" hasActiveFilters={hasConstructionFilters}>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {constructionOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleDisplayOptionChange(option.value)}
                    className={`min-h-14 px-4 py-3 border rounded-lg text-left transition-colors ${
                      tempDisplayOptions.includes(option.value) ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </Section>

            {/* Display options section */}
            <Section title="Zobrazení" hasActiveFilters={hasDisplayFilters}>
              <div className="space-y-2">
                {displayOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleDisplayOptionChange(option.value)}
                    className={`w-full px-4 py-3 border rounded-lg text-left transition-colors ${
                      tempDisplayOptions.includes(option.value) ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </Section>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 sm:rounded-b-2xl">
          <button onClick={handleApplyChanges} className="w-full bg-blue-600 text-white rounded-lg py-3 font-medium hover:bg-blue-700 transition-colors">
            Zobrazit výsledky
          </button>
        </div>
      </div>
    </div>
  )
}

export default FilterModal
